import { Box } from "@mui/material";
import { memo, useMemo, useEffect, useRef, useCallback } from "react";
import ResponsiveSidebar from "./ResponsiveSidebar";
import RightSidebarNav from "./RightSidebarNav";
import NavViewHeader from "./NavViewHeader";
import { useResponsive } from "src/reusable/hooks/useResponsive";
import { UnifiedSidebarProps, SidebarTabId } from "./types";

/**
 * UnifiedSidebar - Reusable sidebar system for all products
 *
 * Features:
 * - Configurable tabs with icons, content, and header actions
 * - Responsive (mobile: bottom drawer, desktop: right drawer + nav bar)
 * - Drag-to-resize on desktop
 * - Badge counts on nav buttons
 * - Flexible state management via props
 * - Product-specific configuration via hooks
 *
 * Usage:
 * ```tsx
 * const { rightSidebar, setRightSidebar } = useUIStore();
 * const config = useProductSidebar();
 * return (
 *   <UnifiedSidebar
 *     config={config}
 *     context={{ artifacts, sources }}
 *     open={rightSidebar.isOpen}
 *     onOpenChange={(open) => setRightSidebar({ isOpen: open })}
 *     selectedTab={rightSidebar.selectedItem}
 *     onTabChange={(tab) => setRightSidebar({ selectedItem: tab })}
 *     width={rightSidebar.width}
 *     onWidthChange={(width) => setRightSidebar({ width })}
 *   />
 * )
 * ```
 */
const UnifiedSidebar = <TContext,>({
  config,
  context,
  open,
  onOpenChange,
  selectedTab: selectedTabProp,
  onTabChange,
  width,
  onWidthChange,
}: UnifiedSidebarProps<TContext>) => {
  const { isMobile } = useResponsive();

  // Determine selected tab (prop > config default)
  const selectedTabId =
    selectedTabProp !== undefined && selectedTabProp !== null
      ? selectedTabProp
      : (config.defaultTab ?? config.tabs[0]?.id ?? null);

  // Use provided width
  const sidebarWidth = width;

  // Stable callbacks for sidebar open/close to prevent unnecessary re-renders
  const handleOpen = useCallback(() => onOpenChange(true), [onOpenChange]);
  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  // Stable callback for computing badge content
  const getBadgeContent = useCallback(
    (tabId: SidebarTabId) => {
      const tab = config.tabs.find((t) => t.id === tabId);
      return tab?.getBadgeContent?.(context) ?? 0;
    },
    [config.tabs, context],
  );

  // Memoize responsive settings
  const responsiveSettings = useMemo(
    () => ({
      enableMobile: config.responsive?.enableMobile ?? true,
      enableDesktopNav: config.responsive?.enableDesktopNav ?? true,
      enableResize: config.responsive?.enableResize ?? true,
      minWidth: config.responsive?.minWidth ?? 300,
      maxWidth: config.responsive?.maxWidth ?? 800,
      defaultWidth: config.responsive?.defaultWidth ?? 400,
    }),
    [config.responsive],
  );

  // Auto-open logic - tracks content changes and auto-opens sidebar when content arrives
  const prevCounts = useRef<Record<string, number>>({});
  const wasManuallyClosedRef = useRef(false);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    if (!config.autoOpen) return;

    const currentCounts = config.autoOpen.getContentCounts(context);
    const autoOpenEnabled =
      typeof config.autoOpen.enabled === "function"
        ? config.autoOpen.enabled()
        : config.autoOpen.enabled;

    // Handle initial mount
    if (isInitialMountRef.current) {
      prevCounts.current = currentCounts;
      isInitialMountRef.current = false;

      // Auto-close on initial mount if no content and auto-close is enabled
      const autoCloseEnabled = config.autoOpen.autoClose ?? true;
      const hasNoContent = Object.values(currentCounts).every(
        (count) => count === 0,
      );
      if (autoCloseEnabled && hasNoContent && open) {
        onOpenChange(false);
        onTabChange(null);
      }
      return;
    }

    // Reset manual close when content resets (new search)
    const triggerKey = config.autoOpen.triggerKey || "sources";
    const contentReset =
      (prevCounts.current[triggerKey] || 0) > 0 &&
      (currentCounts[triggerKey] || 0) === 0;
    if (contentReset) {
      wasManuallyClosedRef.current = false;

      // Auto-close sidebar when content becomes empty (if enabled)
      const autoCloseEnabled = config.autoOpen.autoClose ?? true;
      if (autoCloseEnabled && open) {
        onOpenChange(false);
        onTabChange(null);
      }
    }

    // Auto-open sidebar when trigger content arrives (if enabled)
    const isFirstContent =
      (prevCounts.current[triggerKey] || 0) === 0 &&
      (currentCounts[triggerKey] || 0) > 0;

    if (isFirstContent && !wasManuallyClosedRef.current && autoOpenEnabled) {
      // Only auto-select a tab when the sidebar is currently closed and being opened.
      // If the sidebar is already open, the user has chosen their tab — don't override it.
      if (!open) {
        const tabToSelect =
          config.autoOpen.autoSelectTab ||
          config.defaultTab ||
          config.tabs[0]?.id;
        if (tabToSelect) {
          onTabChange(tabToSelect);
        }
        onOpenChange(true);
      }
    }

    prevCounts.current = currentCounts;
  }, [config.autoOpen, context, open, onOpenChange, onTabChange]);

  // Track manual close
  const prevIsOpenRef = useRef(open);

  useEffect(() => {
    if (!config.autoOpen) return;

    const wasOpen = prevIsOpenRef.current;
    const isNowClosed = !open;
    const currentCounts = config.autoOpen.getContentCounts(context);
    const hasContent = Object.values(currentCounts).some((count) => count > 0);

    if (wasOpen && isNowClosed && hasContent) {
      wasManuallyClosedRef.current = true;
    }

    prevIsOpenRef.current = open;
  }, [open, config.autoOpen, context]);

  // Convert visible tabs to sidebar view format
  const visibleSidebarViews = useMemo(
    () =>
      config.tabs.map((tab) => ({
        id: tab.id,
        label: tab.label,
        icon: tab.icon,
      })),
    [config.tabs],
  );

  // Check if any tabs are configured
  const hasTabs = visibleSidebarViews.length > 0;

  // Get current tab config
  const currentTabConfig = useMemo(
    () => config.tabs.find((tab) => tab.id === selectedTabId),
    [config.tabs, selectedTabId],
  );

  // Render tab content (inline - JSX creation is cheap, re-rendering is what matters)
  const renderContent = () => {
    if (!currentTabConfig) return null;

    return (
      <>
        <NavViewHeader
          text={currentTabConfig.headerLabel || currentTabConfig.label}
        >
          {currentTabConfig.renderHeaderActions?.({ context })}
        </NavViewHeader>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            "& > *": {
              maxWidth: "100%",
              overflowX: "hidden",
            },
          }}
        >
          {currentTabConfig.renderContent({ context })}
        </Box>
      </>
    );
  };

  // Desktop layout: ResponsiveSidebar + RightSidebarNav
  if (!isMobile) {
    const enableNav = responsiveSettings.enableDesktopNav ?? true;
    const shouldShowNav = enableNav && hasTabs;

    return (
      <Box sx={{ display: "flex" }}>
        {shouldShowNav && (
          <RightSidebarNav
            tabs={visibleSidebarViews}
            selectedTabId={selectedTabId}
            onTabChange={onTabChange}
            onOpenSidebar={handleOpen}
            onCloseSidebar={handleClose}
            isSidebarOpen={open}
            getBadgeContent={getBadgeContent}
          />
        )}

        {open && hasTabs && (
          <ResponsiveSidebar
            open={open}
            onClose={handleClose}
            width={sidebarWidth}
            currentView={selectedTabId}
            onViewChange={onTabChange}
            views={visibleSidebarViews}
            resizable={responsiveSettings.enableResize}
            minWidth={responsiveSettings.minWidth}
            maxWidth={responsiveSettings.maxWidth}
            onResize={onWidthChange}
          >
            {renderContent()}
          </ResponsiveSidebar>
        )}
      </Box>
    );
  }

  // Mobile layout: Bottom drawer with tabs
  return (
    <Box>
      {responsiveSettings.enableMobile && hasTabs && (
        <ResponsiveSidebar
          open={open}
          onClose={handleClose}
          onOpen={handleOpen}
          width="100%"
          height="70vh"
          currentView={selectedTabId}
          onViewChange={onTabChange}
          views={visibleSidebarViews}
          resizable={false}
        >
          {renderContent()}
        </ResponsiveSidebar>
      )}
    </Box>
  );
};

export default memo(UnifiedSidebar) as typeof UnifiedSidebar;
